import * as React from "react";
import { formatDateOnly } from "@/common/utils/date";

/**
 * 달력 위젯의 공용 로직.
 *
 * 날짜 선택(DatePicker), 기간 선택(DateRangePicker), 폼 날짜 필드(FormDatePicker)가
 * 같은 그리드 규칙·같은 연월 이동·같은 드롭다운 스크롤을 쓴다. 세 곳이 각자 갖고 있던
 * 코드를 여기로 모았다. 보이는 부분(DayCell)만 화면별로 다르다.
 */

export const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

/** 6주(42칸) 고정 그리드 */
const CALENDAR_CELL_COUNT = 42;

/** 연도 드롭다운에 올릴 범위 (올해 ±10년) */
const YEAR_OPTION_SPAN = 10;

export type DayStatus =
  | "normal"
  | "today"
  | "assistive"
  | "selected"
  | "rangeStart"
  | "rangeEnd"
  | "inRange";

export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export function isSameDay(date1: Date | null, date2: Date | null): boolean {
  if (!date1 || !date2) return false;
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

/** 시작·종료를 뺀 사이 구간. 양끝은 rangeStart·rangeEnd 로 따로 판정한다 */
export function isDateInRange(
  date: Date,
  startDate: Date | null,
  endDate: Date | null,
): boolean {
  if (!startDate || !endDate) return false;
  const time = date.getTime();
  return time > startDate.getTime() && time < endDate.getTime();
}

export interface CalendarCell {
  day: number;
  isCurrentMonth: boolean;
  status: DayStatus;
}

/**
 * 한 달치 달력 칸을 만든다. 앞뒤로 이웃 달을 채워 항상 42칸이다 —
 * 달마다 높이가 바뀌면 팝오버가 흔들린다.
 *
 * 현재 달 날짜의 상태 판정은 화면마다 다르므로 `getStatus` 로 받는다.
 */
export function buildCalendarCells(
  viewYear: number,
  viewMonth: number,
  getStatus: (date: Date) => DayStatus,
): CalendarCell[] {
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const prevMonthDays = getDaysInMonth(
    viewMonth === 0 ? viewYear - 1 : viewYear,
    viewMonth === 0 ? 11 : viewMonth - 1,
  );

  const cells: CalendarCell[] = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({
      day: prevMonthDays - i,
      isCurrentMonth: false,
      status: "assistive",
    });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({
      day,
      isCurrentMonth: true,
      status: getStatus(new Date(viewYear, viewMonth, day)),
    });
  }

  for (let day = 1; day <= CALENDAR_CELL_COUNT - cells.length; day++) {
    cells.push({ day, isCurrentMonth: false, status: "assistive" });
  }

  return cells;
}

/** 연도 드롭다운 옵션 (올해 ±10년) */
export function getYearOptions(): number[] {
  const currentYear = new Date().getFullYear();
  return Array.from(
    { length: YEAR_OPTION_SPAN * 2 + 1 },
    (_, i) => currentYear - YEAR_OPTION_SPAN + i,
  );
}

/** 월 드롭다운 옵션 (0~11) */
export const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => i);

/**
 * 달력이 보고 있는 연·월 상태.
 *
 * 팝오버가 열릴 때 기준일로 맞추는 일은 `focus()` 를 **열기 핸들러에서** 호출해
 * 처리한다 — effect 안에서 setState 하면 열릴 때마다 렌더가 한 번 더 돈다.
 */
export function useCalendarView(initialDate?: Date | null) {
  const base = initialDate ?? new Date();
  const [viewYear, setViewYear] = React.useState(base.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(base.getMonth());

  const focus = (date?: Date | null) => {
    const target = date ?? new Date();
    setViewYear(target.getFullYear());
    setViewMonth(target.getMonth());
  };

  const goPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
      return;
    }
    setViewMonth(viewMonth - 1);
  };

  const goNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
      return;
    }
    setViewMonth(viewMonth + 1);
  };

  return {
    viewYear,
    viewMonth,
    setViewYear,
    setViewMonth,
    /** 기준일이 보이도록 연·월을 맞춘다. 미지정이면 오늘 */
    focus,
    goPrevMonth,
    goNextMonth,
  };
}

/**
 * 연·월 드롭다운이 열릴 때 선택된 항목을 목록 가운데로 올린다.
 * 스크롤은 DOM 조작이라 effect 가 맞다.
 */
export function useScrollToSelectedItem(
  isOpen: boolean,
  listRef: React.RefObject<HTMLDivElement | null>,
) {
  React.useEffect(() => {
    if (!isOpen || !listRef.current) return;
    listRef.current
      .querySelector('[data-selected="true"]')
      ?.scrollIntoView({ block: "center" });
  }, [isOpen, listRef]);
}

/** 달력이 쓰는 날짜 표기. `common/utils/date` 의 단일 구현을 그대로 쓴다 */
export { formatDateOnly };
